<template>
  <div class="number-theory-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-nt-pro"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Mode: Parity (奇偶性) -->
           <g v-if="mode === 'parity'" transform="translate(400, 300)">
              <g v-for="i in currentNum" :key="'p'+i" :transform="`translate(${((i-1)%4-1.5)*65}, ${Math.floor((i-1)/4-1.5)*65})`">
                 <rect width="55" height="55" :fill="(i % 2 === 0) ? '#38bdf8' : '#f43f5e'" rx="8" />
                 <text x="27.5" y="35" fill="#fff" font-size="24" text-anchor="middle" font-weight="900">{{ i }}</text>
                 <line v-if="i % 2 === 0" x1="-65" y1="27.5" x2="0" y2="27.5" stroke="#f8fafc" stroke-width="4" stroke-dasharray="4,4" />
              </g>
           </g>

           <!-- Mode: Factors/Primes (质合与因子) -->
           <g v-if="mode === 'factors'" transform="translate(400, 300)">
              <circle cx="0" cy="0" r="160" fill="rgba(16, 185, 129, 0.05)" stroke="#10b981" stroke-width="3" stroke-dasharray="12,6" />
              <circle cx="0" cy="0" r="80" fill="rgba(16, 185, 129, 0.1)" stroke="#10b981" stroke-width="3" />
              
              <text x="0" y="10" fill="#f8fafc" font-size="48" font-weight="900" text-anchor="middle">{{ currentNum }}</text>

              <!-- Circle nodes for factors -->
              <g v-for="(f, idx) in factors" :key="'f'+f" :transform="`rotate(${idx * (360/factors.length)})`">
                 <line 0 y1="-80" 0 y2="-160" stroke="#94a3b8" stroke-width="2" />
                 <circle cx="0" cy="-160" r="28" fill="#1e293b" stroke="#34d399" stroke-width="3" />
                 <text x="0" y="-152" fill="#34d399" font-size="20" font-weight="900" text-anchor="middle">{{ f }}</text>
              </g>

              <g transform="translate(0, 250)">
                 <text x="0" y="0" fill="#94a3b8" font-size="20" font-weight="900" text-anchor="middle">质因数分解树 (Prime Factorization)</text>
              </g>
           </g>

           <!-- Mode: Modulo (同余/进制) -->
           <g v-if="mode === 'modulo'" transform="translate(80, 50)">
              <g v-for="i in Math.min(currentNum, 60)" :key="'m'+i" 
                 :transform="`translate(${(i-1)%10 * 65}, ${Math.floor((i-1)/10) * 65})`">
                 <rect width="55" height="55" :fill="(i % modVal === 0) ? '#facc15' : '#1e293b'" :stroke="(i % modVal === 0) ? '#fcd34d' : '#475569'" stroke-width="2" rx="10" />
                 <text x="27.5" y="35" :fill="(i % modVal === 0) ? '#713f12' : '#94a3b8'" font-size="22" text-anchor="middle" font-weight="900">{{ i }}</text>
                 
                 <!-- Draw connecting cycle lines for multiples -->
                 <circle v-if="i % modVal === 0" cx="27.5" cy="27.5" r="30" fill="none" stroke="#facc15" stroke-width="4" filter="url(#glow-nt-pro)" opacity="0.5"/>
              </g>
              <g transform="translate(320, 480)">
                 <text x="0" y="0" fill="#facc15" font-size="52" text-anchor="middle" font-weight="900" filter="url(#glow-nt-pro)">
                    {{ currentNum }} ≡ {{ currentNum % modVal }} (mod {{ modVal }})
                 </text>
              </g>
           </g>

           <!-- Mode: Perfect Squares (完全平方数) -->
           <g v-if="mode === 'squares'" transform="translate(400, 300)">
              <!-- Draw the root grid -->
              <g :transform="`translate(${-Math.sqrt(nearestSquare)/2 * 60}, ${-Math.sqrt(nearestSquare)/2 * 60})`">
                 <g v-for="r in Math.sqrt(nearestSquare)" :key="'sr'+r">
                    <g v-for="c in Math.sqrt(nearestSquare)" :key="'sc'+c">
                       <rect :x="(c-1)*60" :y="(r-1)*60" width="55" height="55" fill="#a855f7" rx="8" />
                    </g>
                 </g>
              </g>
              <!-- Show leftover items if currentNum is not perfect square -->
              <g v-if="currentNum > nearestSquare" transform="translate(0, 220)">
                 <rect v-for="k in (currentNum - nearestSquare)" :key="'rem'+k" :x="-((currentNum - nearestSquare)/2 * 30) + (k-1)*30" y="0" width="25" height="25" fill="#ef4444" rx="4" />
                 <text x="0" y="45" fill="#fca5a5" font-size="18" font-weight="900" text-anchor="middle">多出 {{ currentNum - nearestSquare }} 个凑不成正方形</text>
              </g>
              <!-- Math notation -->
              <text x="0" y="-220" fill="#d8b4fe" font-size="32" font-weight="900" text-anchor="middle">面积模型: {{ nearestSquare }} = {{ Math.sqrt(nearestSquare) }} × {{ Math.sqrt(nearestSquare) }} = {{ Math.sqrt(nearestSquare) }}²</text>
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">算术与代数结构解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前数值状态计算</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>研究对象 (n)</span><span class="hl text-blue">{{ currentNum }}</span></div>
                  
                  <template v-if="mode === 'parity'">
                     <div class="data-row"><span>除以 2 结果</span><span class="hl">{{ (currentNum / 2).toFixed(1) }}</span></div>
                     <div class="data-row" :style="{ background: currentNum % 2 === 0 ? 'rgba(56,189,248,0.1)' : 'rgba(244,63,94,0.1)' }">
                        <span>奇偶平移属性</span><span class="hl" :class="currentNum % 2 === 0 ? 'text-blue' : 'text-red'">{{ currentNum % 2 === 0 ? '偶数 (Even)' : '奇数 (Odd - 破缺项)' }}</span>
                     </div>
                  </template>

                  <template v-if="mode === 'factors'">
                     <div class="data-row"><span>因子总数</span><span class="hl text-green">{{ factors.length }} 个</span></div>
                     <div class="data-row"><span>质因数分解展开</span><span class="hl text-green" style="font-size: 13px;">{{ primeFactors.join(' × ') || currentNum }}</span></div>
                     <div class="data-row"><span>数域评判</span><span class="hl" :class="isPrime ? 'text-red' : 'text-blue'">{{ currentNum===1 ? '非质非合' : (isPrime ? '质数 (不可再分)' : '合数 (Composite)') }}</span></div>
                  </template>

                  <template v-if="mode === 'modulo'">
                     <div class="data-row"><span>模周期 (m)</span><span class="hl text-yellow">{{ modVal }}</span></div>
                     <div class="data-row"><span>被除完整商 (k)</span><span class="hl">{{ Math.floor(currentNum / modVal) }}</span></div>
                     <div class="data-row" style="background: rgba(250,204,21,0.1)">
                        <span>同余余数 (r)</span><span class="hl" style="color: #facc15">{{ currentNum % modVal }}</span>
                     </div>
                  </template>

                  <template v-if="mode === 'squares'">
                     <div class="data-row"><span>正数算术平方根</span><span class="hl">{{ Math.sqrt(currentNum).toFixed(3) }}</span></div>
                     <div class="data-row"><span>向下取整底层</span><span class="hl">{{ Math.floor(Math.sqrt(currentNum)) }}</span></div>
                     <div class="data-row" :style="{ background: isPerfectSquare ? 'rgba(168,85,247,0.1)' : 'transparent' }">
                        <span>完全平方数判定</span><span class="hl" :class="isPerfectSquare ? 'text-purple' : 'text-red'">{{ isPerfectSquare ? 'TRUE (完全平方数)' : 'FALSE' }}</span>
                     </div>
                  </template>

               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2; display: flex; flex-direction: column; width: 100%;">
               <label>研究视界 (模型切换)</label>
               <el-radio-group v-model="mode" size="small" style="display: flex; flex-wrap: wrap;">
                 <el-radio-button label="parity">奇偶对立模型</el-radio-button>
                 <el-radio-button label="factors">质合与因子树</el-radio-button>
                 <el-radio-button label="modulo">模算术(同余)分析</el-radio-button>
                 <el-radio-button label="squares">完全平方数阵列</el-radio-button>
               </el-radio-group>
            </div>

            <div class="s-item" style="grid-column: span 2;">
               <label>探究数值 n (当前: {{ currentNum }})</label>
               <el-slider v-model="currentNum" :min="1" :max="mode === 'modulo' ? 60 : 64" :step="1" size="small"></el-slider>
            </div>
            
            <div class="s-item" v-if="mode === 'modulo'">
               <label>除数 / 模 m ({{ modVal }})</label>
               <el-slider v-model="modVal" :min="2" :max="15" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NumberTheoryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'parity',
      currentNum: 16,
      modVal: 5
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       const maps = {
          parity: '奇数与偶数：自然数列中的最基本二元分类律',
          factors: '因式与质数：自然数不可撼动的“原子乘法结构”',
          modulo: '同余与剩余类：“时钟算术”里的无限循环与重置',
          squares: '完全平方数：从几何正方形面积跨越到代数整除特性的奇迹'
       };
       return maps[this.mode] || '数论规律探秘';
    },
    activeFormula() {
       if (this.mode === 'parity') return 'n = 2k (+ 1)';
       if (this.mode === 'factors') return 'n = p₁ᵃ¹ p₂ᵃ²...';
       if (this.mode === 'modulo') return 'a ≡ r (mod m)';
       if (this.mode === 'squares') return 'n = m² | p_i^2k';
       return 'n ∈ Z';
    },
    isPrime() {
       if (this.currentNum < 2) return false;
       for (let i = 2; i <= Math.sqrt(this.currentNum); i++) {
          if (this.currentNum % i === 0) return false;
       }
       return true;
    },
    factors() {
       let f = [];
       for (let i = 1; i <= this.currentNum; i++) {
          if (this.currentNum % i === 0) f.push(i);
       }
       return f;
    },
    primeFactors() {
       let n = this.currentNum;
       let pf = [];
       let d = 2;
       while (n > 1) {
          if (n % d === 0) {
             pf.push(d);
             n /= d;
          } else { d++; }
       }
       return pf;
    },
    isPerfectSquare() {
       return Math.sqrt(this.currentNum) % 1 === 0;
    },
    nearestSquare() {
       return Math.pow(Math.floor(Math.sqrt(this.currentNum)), 2);
    },
    tipDesc() {
       if (this.mode === 'parity') return '在自然数中，偶数仿佛天生就能完美配对，而奇数总是形单影只多出一个1。在运算中，奇偶性具有极其坚挺的“封闭守恒”特质：比如奇加奇必然为偶！';
       if (this.mode === 'factors') return '算术基本定理指出：任何一个大于1的自然数，都可以唯一地分解为质数的乘积。这就是为什么说这帮孤高的质数是构成所有数学数字的“终极原子”。';
       if (this.mode === 'modulo') return '同余算术屏蔽了数量的大小累积，只关注整除后的“余数规律”。比如星期（模7）、钟表（模12），无论数字多大，都会乖乖回退到有限的剩余类抽屉里。正是同余成就了大质数验证和现代密码学！';
       if (this.mode === 'squares') return '一个数是完全平方数，不仅意味着你在几何上能把它用单位方块拼成一个完美的正方形大阵，更在代数上意味着：**它的所有质因数分解项的指数必定全都是偶数**！也正因如此，它的所有正因子的总个数必然是一个奇数（唯一的特例）！';
       return '';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/奇|偶/.test(kp)) this.mode = 'parity';
      else if (/平方/.test(kp)) this.mode = 'squares';
      else if (/同余|余数|循环小数|进制/.test(kp)) this.mode = 'modulo';
      else if (/质|合|因子|分解|余类/.test(kp)) this.mode = 'factors';
      else this.mode = 'parity';
    }
  }
}
</script>

<style scoped lang="scss">
.number-theory-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-purple { color: #a855f7; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; } .text-yellow { color: #facc15; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
